import { ContentService } from '@blk/cwp-ng-framework';
import { chain } from 'lodash';

import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { ColumnExtractor } from '../extractors/column-extractor';
import { StoreColumnExtractor } from '../extractors/store-column-extractor';
import { SeriesMapper } from '../series-mappers/series-mapper.interface';
import { TableData } from '../table-data';
import { AsIsTableDataTransform } from '../table-data-transform/as-is-table-data-transform';
import { TableDataTransform } from '../table-data-transform/table-data-transform';
import { SeriesExtractionExtractorConfig } from './series-extractor-config';

export class CombinedPortfoliosMultiColumnGridExtractorConfig extends SeriesExtractionExtractorConfig {

	public static create(options: CombinedPortfoliosMultiColumnGridExtractorConfigOptions): CombinedPortfoliosMultiColumnGridExtractorConfig {
		return new this(
			options.mapper,
			options.firstColumnTitle,
			options.portfolioName,
			options.category,
			options.value,
			options.otherColumns,
			options.tableDataTransform,
			options.batchSize,
			options.portfolioFilter,
			options.overrideHeaderXId
		);
	}


	/**
	 * @deprecated Use CombinedPortfoliosMultiColumnGridExtractorConfig.create() instead
	 */
	constructor(
		mapper: SeriesMapper,
		public firstColumnTitle: ColumnText,
		portfolioName: ColumnConfig<StoreColumnExtractor>,
		public readonly category: ColumnConfig<ColumnExtractor>, // uniqueid
		public readonly value: ColumnConfig<ColumnExtractor>,
		public readonly otherColumns: ColumnConfig<ColumnExtractor>[],
		tableDataTransform: TableDataTransform = AsIsTableDataTransform.instance,
		private batchSize = Number.MAX_VALUE,
		private portfolioFilter = (port: any) => true,
		private overrideHeaderXId?: string
	) {
		super(portfolioName, mapper, null, tableDataTransform);
	}

	public storeDependencies(): string[] {
		return this.mapper.getStoreDependencies();
	}

	public extract(storeLookup: Map<string, any[]>, contentSvc: ContentService): TableData[] {
		const dataArgs = this.mapper.getStoreDependencies().map(config => storeLookup.get(config));
		const filteredIndicies = [];
		let i = 0;

		const portsDataArgs: any[] = chain(dataArgs)
			.unzip()
			.filter(data => {
				if (!this.portfolioFilter(data)) {
					filteredIndicies.push(i);
					i++;
					return false;
				}
				i++;
				return true;
			})
			.value();
		const filteredLookup = new Map();
		storeLookup.forEach((values: any[], key) => {
			filteredLookup.set(key, values.filter((v, i) => !filteredIndicies.includes(i)));
		});
		const portfolioNames: CellData[] = this.extractStoreColumn(this.portfolioName, filteredLookup, contentSvc);

		const results = [];
		while (portsDataArgs.length > 0) {
			results.push(this.extractBatch(portsDataArgs.splice(0, this.batchSize), contentSvc, portfolioNames.splice(0, this.batchSize)));
		}
		return results;
	}

	private extractBatch(portsDataArgs: any[], contentSvc: ContentService, portfolioNames: CellData[]): TableData {
		const categoriesUniverse: Map<string, CellData> = new Map();
		const categoriesPerPortfolio: string[][] = [];
		const portfoliosValues: Map<string, CellData>[] = [];
		const otherColumnsPerCategory: Map<string, CellData[]> = new Map();
		const otherColumnNames: CellData[][] = [[]];

		portsDataArgs.forEach((portfolioData, portNo) => {
			portfoliosValues.push(new Map());
			categoriesPerPortfolio.push([]);

			this.mapper.mapper(...portfolioData).forEach(data => {
				const category = this.category.extractor.extractor()(data);
				const categoryCell = new CellData(
					this.category,
					category,
					this.category.labelFormatter.format(category, contentSvc),
					null,
					this.generateAttributes(category, this.category, contentSvc)
				);

				const otherColumns = this.otherColumns.map(column => column.extractor.extractor()(data));
				const otherColumnsCells = otherColumns.map(
					(column, index) =>
						new CellData(
							this.otherColumns[index],
							column,
							this.otherColumns[index].labelFormatter.format(column, contentSvc),
							null,
							this.generateAttributes(column, this.otherColumns[index], contentSvc)
						)
				);
				otherColumnsPerCategory.set(category, otherColumnsCells);

				categoriesPerPortfolio[portNo].push(category);
				if (!categoriesUniverse.has(category)) {
					categoriesUniverse.set(category, categoryCell);
				}

				const valueCell = this.extractDataColumn(this.value, data, contentSvc, null);
				const cellMap = portfoliosValues[portNo];
				if (!cellMap.has(category) || typeof cellMap.get(category).rawValue !== 'number') {
					cellMap.set(category, valueCell);
				} else {
					const oldValue = cellMap.get(category);
					const aggValue = Number(oldValue.rawValue) + Number(valueCell.rawValue);
					cellMap.set(category, new CellData(oldValue.config, aggValue, oldValue.config.labelFormatter.format(aggValue), oldValue.parent, oldValue.attribute));
				}
			});
		});

		// 1. establish the order of rows / series by merging categories
		const categoriesList = chain(categoriesPerPortfolio)
			.flatten()
			.sortBy(v => v) // the category sort order needs to be configurable -> maybe should be done at a later stage in the row transform
			.sortedUniq()
			.value();

		// reconstruct other columns after categoriesList is sorted
		categoriesList.forEach(category => {
			otherColumnsPerCategory.get(category).forEach((column, index) => {
				if (!otherColumnNames[index]) { otherColumnNames[index] = []; }
				otherColumnNames[index].push(column);
			});
		});

		// reconstruct the cells grid with defaults for missing entries
		const cells: CellData[][] = [
			...otherColumnNames,
			...portfoliosValues.map(portfolioValues => {
				const cells = categoriesList.map(category => {
					const value = portfolioValues.get(category);
					return value ? value : this.extractDataColumn(this.value, null, contentSvc, null);
				});

				return cells;
			})
		];

		// reconstruct the column names
		let columnNames = categoriesList.map(cat => categoriesUniverse.get(cat));
		if (this.overrideHeaderXId) {
			columnNames = [];
			cells.forEach(cellDataForAllPorts => columnNames.push(...cellDataForAllPorts.filter(cellData => cellData.config.id === this.overrideHeaderXId)));
			columnNames.filter(cellData => !!cellData);
		}

		// fill up headersX with the other column headers
		const otherColumnHeaders: CellData[] = this.generateColumnHeaders(this.otherColumns, contentSvc);
		const headersY: CellData[] = otherColumnHeaders.concat(portfolioNames);

		const tableData = new TableData(columnNames, headersY, cells, portfolioNames, this.translateColumnText(this.firstColumnTitle, contentSvc));
		const transformedCells: TableData = this.tableDataTransform.transform(tableData, contentSvc);

		return transformedCells;
	}
}

export interface CombinedPortfoliosMultiColumnGridExtractorConfigOptions {
	mapper: SeriesMapper;
	firstColumnTitle: ColumnText;
	portfolioName: ColumnConfig<StoreColumnExtractor>;
	category: ColumnConfig<ColumnExtractor>;
	value: ColumnConfig<ColumnExtractor>;
	otherColumns: ColumnConfig<ColumnExtractor>[];
	tableDataTransform?: TableDataTransform;
	batchSize?: number;
	portfolioFilter?: (port: any) => boolean;
	overrideHeaderXId?: string;
}
